// Thin fetch wrapper with credentials included so the session cookie travels
// on every request. All /api/me, /api/journal, /api/progress traffic flows
// through here so the typed contracts stay in one place.

import type {
  ChatHistoryResponse,
  ClaimInput,
  JournalCreateInput,
  JournalEntryDto,
  JournalListResponse,
  MeBundleDto,
  ProgressDto,
  PushSubscriptionDto,
  RecordSessionInput,
  SaveChatInput,
  SendTestPushResult,
  SubmitPersonalityInput,
  SubmitPersonalityResult,
  UpdateCoachInput,
  UpdateCovenantInput,
  UpdateNotificationPrefsInput,
  UpdateRitualInput,
  UpdateUserInput,
  VapidPublicKeyResponse,
} from '@llura/types';

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init.headers },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  me: {
    bundle: () => request<MeBundleDto>('/me'),
    updateUser: (input: UpdateUserInput) =>
      request<void>('/me/user', { method: 'PATCH', body: JSON.stringify(input) }),
    updateCoach: (input: UpdateCoachInput) =>
      request<void>('/me/coach', { method: 'PATCH', body: JSON.stringify(input) }),
    updateCovenant: (input: UpdateCovenantInput) =>
      request<void>('/me/covenant', { method: 'PATCH', body: JSON.stringify(input) }),
    updateRitual: (input: UpdateRitualInput) =>
      request<void>('/me/ritual', { method: 'PATCH', body: JSON.stringify(input) }),
    updateNotificationPrefs: (input: UpdateNotificationPrefsInput) =>
      request<void>('/me/notification-prefs', {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    claim: (input: ClaimInput) =>
      request<void>('/me/claim', { method: 'POST', body: JSON.stringify(input) }),
    submitPersonality: (input: SubmitPersonalityInput) =>
      request<SubmitPersonalityResult>('/me/personality', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    skipPersonality: () =>
      request<void>('/me/personality/skip', { method: 'POST' }),
    resetPersonality: () =>
      request<void>('/me/personality/reset', { method: 'POST' }),
  },
  journal: {
    list: (cursor?: string | null, limit = 50) => {
      const params = new URLSearchParams();
      if (cursor) params.set('cursor', cursor);
      params.set('limit', String(limit));
      return request<JournalListResponse>(`/journal?${params.toString()}`);
    },
    create: (input: JournalCreateInput) =>
      request<JournalEntryDto>('/journal', { method: 'POST', body: JSON.stringify(input) }),
    delete: (id: string) => request<void>(`/journal/${id}`, { method: 'DELETE' }),
  },
  progress: {
    recordSession: (input: RecordSessionInput) =>
      request<ProgressDto>('/progress/sessions', { method: 'POST', body: JSON.stringify(input) }),
  },
  chat: {
    history: () => request<ChatHistoryResponse>('/chat/history'),
    save: (input: SaveChatInput) =>
      request<void>('/chat/history', { method: 'PUT', body: JSON.stringify(input) }),
    clear: () => request<void>('/chat/history', { method: 'DELETE' }),
  },
  notifications: {
    vapidKey: () => request<VapidPublicKeyResponse>('/notifications/vapid-public-key'),
    subscribe: (dto: PushSubscriptionDto) =>
      request<void>('/notifications/subscribe', {
        method: 'POST',
        body: JSON.stringify(dto),
      }),
    testSelf: () =>
      request<SendTestPushResult>('/notifications/test-self', { method: 'POST' }),
  },
};
